import React from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import PhaseHeader from "@/components/PhaseHeader";
import PhaseFooter from "@/components/PhaseFooter";
import AnimatedNumber from "@/components/AnimatedNumber";
import { computeSavings } from "@/lib/simulation-defaults";
import { Clock, Euro, ArrowDown } from "lucide-react";

function Bar({ label, value, max, color, testId, unit = "", extra = null }) {
  const widthPct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] w-20 shrink-0 leading-tight">
        {label}
      </span>
      <div className="flex-1 h-5 bg-[color:var(--surface-2)] border border-[color:var(--border)] relative overflow-hidden rounded-sm">
        <div
          className="bar-fill h-full absolute inset-y-0 left-0"
          style={{ width: `${widthPct}%`, background: color }}
        />
      </div>
      <span className="font-mono text-[11px] text-[color:var(--text)] w-24 text-right tabular-num leading-tight">
        <span className="block">
          {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit ? ` ${unit}` : ""}
        </span>
        {extra ? (
          <span className="block text-[9px] text-[color:var(--text-mute)]">{extra}</span>
        ) : null}
      </span>
    </div>
  );
}

/** Convert minutes to a "H,HH h" localised string. */
function minToH(min) {
  const h = (Number(min) || 0) / 60;
  return `${h.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
}

export default function Phase4Comparison() {
  const { state } = useSimulation();
  const { t } = useI18n();

  const s = computeSavings(state);
  const { cb, pb, ct, pt } = s;
  const doors = s.doors;

  // Totals (computed) — no hardcoded values
  const totalCustomer = s.priceCustomer;
  const totalPrimo = s.pricePrimo;
  const maxPrice = Math.max(totalCustomer, totalPrimo, 1);

  // Time bars: show full-set values (per frame × doors), in minutes
  const ctSet = { cutting: ct.cutting * doors, gluing: ct.gluing * doors, mounting: ct.mounting * doors };
  const ptSet = { welding: pt.welding * doors, gluing: pt.gluing * doors, mounting: pt.mounting * doors };
  const maxTime = Math.max(
    ctSet.mounting, ctSet.gluing, ctSet.cutting,
    ptSet.mounting, ptSet.gluing, ptSet.welding,
    1
  );

  const priceSavings = s.priceSavings;
  const timeSavingsMin = s.timeSavingsMin;
  const timeSavingsH = s.timeSavingsH;

  const dangerColor = "linear-gradient(90deg,#8b0000,#b91c1c)";
  const brandColor = "linear-gradient(90deg,#00B2FF,#0090CC)";

  const money2 = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="wizard-phase phase-in max-w-7xl mx-auto px-4 sm:px-6">
      <PhaseHeader
        eyebrow={t("phases.p4")}
        title={t("compare.title")}
        subtitle={t("compare.subtitle")}
      />

      <div className="wizard-body">
        <div className="wizard-body-scroll grid grid-cols-1 lg:grid-cols-2 gap-3 auto-rows-min">
          {/* CUSTOMER SIDE */}
          <div className="card-tech p-3 lg:p-4 relative overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[color:var(--text-mute)]">
                  {t("compare.current")}
                </div>
                <h3 className="font-display font-bold text-base text-[color:var(--text)] leading-tight">Customer</h3>
              </div>
              <div className="w-1.5 h-8 bg-[color:var(--danger)]" />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3 pb-3 border-b border-[color:var(--border)]">
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] flex items-center gap-1">
                  <Euro className="w-2.5 h-2.5" />
                  {t("customer.totalPrice")}
                </div>
                <AnimatedNumber
                  value={totalCustomer}
                  decimals={2}
                  prefix="€ "
                  className="text-2xl font-bold text-[color:var(--text)] block leading-none"
                  data-testid="compare-customer-price"
                />
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {t("customer.totalTime")}
                </div>
                <AnimatedNumber
                  value={s.timeCustomerH}
                  decimals={2}
                  suffix=" h"
                  className="text-2xl font-bold text-[color:var(--text)] block leading-none"
                  data-testid="compare-customer-time"
                />
                <div className="text-[9px] font-mono text-[color:var(--text-mute)] tabular-num">
                  = {s.timeCustomerMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} min
                </div>
              </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2">
              {t("compare.priceBreakdown")}
            </div>
            <div className="space-y-1.5 mb-3">
              <Bar label={t("compare.profiles")} value={cb.profiles} max={maxPrice} color={dangerColor} testId="c-bar-profiles" unit="€" />
              <Bar label={t("compare.labor")} value={cb.labor} max={maxPrice} color={dangerColor} testId="c-bar-labor" unit="€" />
              <Bar label={t("compare.steelband")} value={cb.steelband} max={maxPrice} color={dangerColor} testId="c-bar-steel" unit="€" />
            </div>

            <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2">
              {t("compare.timeBreakdown")} · min · {doors} portas
            </div>
            <div className="space-y-1.5">
              <Bar label={t("customer.cuttingTime")} value={ctSet.cutting} max={maxTime} color={dangerColor} testId="c-bar-cut-time" unit="min" extra={minToH(ctSet.cutting)} />
              <Bar label={t("customer.gluing")} value={ctSet.gluing} max={maxTime} color={dangerColor} testId="c-bar-gluing-time" unit="min" extra={minToH(ctSet.gluing)} />
              <Bar label={t("customer.mounting")} value={ctSet.mounting} max={maxTime} color={dangerColor} testId="c-bar-mount-time" unit="min" extra={minToH(ctSet.mounting)} />
            </div>
          </div>

          {/* PRIMO SIDE */}
          <div className="card-tech p-3 lg:p-4 relative overflow-hidden border-[color:var(--brand)]/60">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[color:var(--brand)]">
                  {t("compare.primoSolution")}
                </div>
                <h3 className="font-display font-bold text-base text-[color:var(--text)] leading-tight">Primo</h3>
              </div>
              <div className="w-1.5 h-8 bg-[color:var(--brand)]" />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3 pb-3 border-b border-[color:var(--border)]">
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] flex items-center gap-1">
                  <Euro className="w-2.5 h-2.5" />
                  {t("primo.totalPrice")}
                </div>
                <AnimatedNumber
                  value={totalPrimo}
                  decimals={2}
                  prefix="€ "
                  className="text-2xl font-bold text-[color:var(--brand)] block leading-none"
                  data-testid="compare-primo-price"
                />
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {t("primo.totalTime")}
                </div>
                <AnimatedNumber
                  value={s.timePrimoH}
                  decimals={2}
                  suffix=" h"
                  className="text-2xl font-bold text-[color:var(--brand)] block leading-none"
                  data-testid="compare-primo-time"
                />
                <div className="text-[9px] font-mono text-[color:var(--text-mute)] tabular-num">
                  = {s.timePrimoMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} min
                </div>
              </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2">
              {t("compare.priceBreakdown")}
            </div>
            <div className="space-y-1.5 mb-3">
              <Bar label={t("compare.profiles")} value={pb.profiles} max={maxPrice} color={brandColor} testId="p-bar-profiles" unit="€" />
              <Bar label={t("compare.labor")} value={pb.labor} max={maxPrice} color={brandColor} testId="p-bar-labor" unit="€" />
              <Bar label={t("compare.materials")} value={pb.materials} max={maxPrice} color={brandColor} testId="p-bar-materials" unit="€" />
            </div>

            <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2">
              {t("compare.timeBreakdown")} · min · {doors} portas
            </div>
            <div className="space-y-1.5">
              <Bar label={t("primo.weldingCutting")} value={ptSet.welding} max={maxTime} color={brandColor} testId="p-bar-weld-time" unit="min" extra={minToH(ptSet.welding)} />
              <Bar label={t("primo.gluing")} value={ptSet.gluing} max={maxTime} color={brandColor} testId="p-bar-gluing-time" unit="min" extra={minToH(ptSet.gluing)} />
              <Bar label={t("primo.mounting")} value={ptSet.mounting} max={maxTime} color={brandColor} testId="p-bar-mount-time" unit="min" extra={minToH(ptSet.mounting)} />
            </div>
          </div>

          {/* Delta strip */}
          <div className="card-tech p-3 lg:p-4 lg:col-span-2 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <ArrowDown className="w-6 h-6 text-[color:var(--brand)]" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)]">
                  {t("reveal.pricePerSet")}
                </div>
                <div className="font-display font-bold text-2xl text-[color:var(--brand)] leading-tight" data-testid="delta-price">
                  € {money2(priceSavings)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <ArrowDown className="w-6 h-6 text-[color:var(--brand)]" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)]">
                  {t("reveal.timePerSet")}
                </div>
                <div className="font-display font-bold text-2xl text-[color:var(--brand)] leading-tight" data-testid="delta-time">
                  {timeSavingsH.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
                </div>
                <div className="text-[10px] font-mono text-[color:var(--text-mute)] tabular-num">
                  = {timeSavingsMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} min
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PhaseFooter isFinal nextLabel={t("common.finish")} nextTestId="phase4-continue-btn" />
    </div>
  );
}
